import { apiFetch, clearSession, refreshSession } from "./apiClient.js";
import { Customers } from "./customerUtils.js";

async function getMachines(locale) {
  const params = locale ? `?language=${encodeURIComponent(locale)}` : "";
  const response = await apiFetch(`/api/machines${params}`, {
    method: "GET",
  });

  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to get machines"),
    };
  }

  return await response.json();
}

async function createSellProposal(sellProposal) {
  const response = await apiFetch("/api/sell-proposals", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(sellProposal),
  });

  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to create sell proposal"),
    };
  }

  return response;
}

async function contact(message) {
  const response = await apiFetch("/api/contact", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(message),
  });

  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to send message"),
    };
  }

  return response;
}

async function resendOtp() {
  const response = await apiFetch("/api/users/otp/resend", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to resend otp"),
    };
  }

  return response;
}

async function sendForgotPasswordOtp(email) {
  const response = await apiFetch("/api/users/otp/forgot-password", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email,
    }),
  });

  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to send otp"),
    };
  }

  return response;
}

async function verifyOtp(otp) {
  const response = await apiFetch("/api/users/otp/verify", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      otp: otp,
    }),
  });

  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to verify otp"),
    };
  }

  return response;
}

async function verifyForgotPasswordOtp(otp, email, newPassword) {
  const response = await apiFetch("/api/users/otp/forgot-password/verify", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      otp,
      email,
      newPassword,
    }),
  });

  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to verify otp"),
    };
  }

  return response;
}

async function login(loginRequest) {
  const response = await apiFetch("/api/users/login", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(loginRequest),
  });

  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to login"),
    };
  }

  return response;
}

async function register(registerRequest) {
  const response = await apiFetch("/api/users/register", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(registerRequest),
  });

  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to register"),
    };
  }

  return response;
}

/**
 * Trade the refresh cookie for a fresh access token. Also updates the session held by apiClient.
 * @returns {Promise<{ jwt: string, email: string, roles: string[], verified: boolean, expiresIn: number }|null>}
 *   null when there is no session (no cookie, or one that is unknown, revoked, expired or unverified):
 *   the session is cleared and the visitor is anonymous.
 * @throws {{ status: number, error: Error }} on a network error or any other non-2xx answer.
 */
function refresh() {
  return refreshSession();
}

/**
 * Ends the session on the server (revokes the refresh token and clears the cookie), then forgets
 * the access token here. The local session is cleared even when the request fails.
 */
async function logout() {
  let response;
  try {
    response = await apiFetch("/api/users/logout", {
      method: "POST",
    });
  } finally {
    clearSession();
  }

  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to log out"),
    };
  }
}

async function createMachine({ machine }) {
  const response = await apiFetch("/api/admin/machines", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(machine),
  });

  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to create machine"),
    };
  }

  return response;
}

async function deleteMachine({ id }) {
  const response = await apiFetch(`/api/admin/machines/${id}`, {
    method: "DELETE",
  });

  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to delete machine"),
    };
  }

  return response;
}

async function updateMachine({ id, machine }) {
  const response = await apiFetch(`/api/admin/machines/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(machine),
  });

  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to update machine"),
    };
  }

  return response;
}

/**
 * @returns {Promise<string>} url to the uploaded file
 * @throws {{ statusCode, error }}
 */
async function uploadFile({ file }) {
  const formData = new FormData();
  formData.append("file", file);

  const response = await apiFetch("/api/admin/file/upload", {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to upload file"),
    };
  }

  const json = await response.json();
  return json.url;
}

async function getSellProposals() {
  const response = await apiFetch("/api/admin/sell-proposals", {});

  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to get sell proposals"),
    };
  }

  return await response.json();
}

async function publishMachine({ id }) {
  const response = await apiFetch(`/api/admin/machines/${id}/publish`, {
    method: "PUT",
  });

  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to publish machine"),
    };
  }

  return;
}

async function unpublishMachine({ id }) {
  const response = await apiFetch(`/api/admin/machines/${id}/unpublish`, {
    method: "PUT",
  });

  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to unpublish machine"),
    };
  }

  return;
}

async function addSellProposalEvent({ sellProposalId, description }) {
  const response = await apiFetch("/api/admin/sell-proposals/events", {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      sellProposalId,
      description,
    }),
  });

  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to add sell proposal event"),
    };
  }

  return await response.json();
}

async function createMachineSale({ machineId, customerId, soldAt, salePrice, currency, imageUrl }) {
  const response = await apiFetch("/api/admin/machine-sales", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ machineId, customerId, soldAt, salePrice, currency, imageUrl }),
  });

  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to create machine sale"),
    };
  }

  return await response.json();
}

async function deleteMachineSale({ saleId }) {
  const response = await apiFetch(`/api/admin/machine-sales/${saleId}`, {
    method: "DELETE",
  });

  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to delete machine sale"),
    };
  }
}

async function getMachineSales() {
  const response = await apiFetch("/api/admin/machine-sales", {});

  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to get machine sales"),
    };
  }

  return await response.json();
}

async function getRecentSales(limit = 10) {
  const response = await apiFetch(`/api/machine-sales/recent?limit=${limit}`);

  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to get recent sales"),
    };
  }

  return await response.json();
}

/**
 * @returns {Promise<NewsArticle[]>}
 * @throws {{ status: number, error: Error }} status code and error.
 */
async function getNews(locale) {
  const params = locale ? `?language=${locale}` : "";
  const response = await apiFetch(`/api/news${params}`, {
    method: "GET",
  });

  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to get news"),
    };
  }

  const news = await response.json();
  // Map articles to have dates
  // (createdAt, updatedAt) instead of strings
  return news.map((article) => ({
    ...article,
    createdAt: new Date(article.createdAt),
    updatedAt: new Date(article.updatedAt),
  }));
}

/**
 * @param {{
 *  authorName: string,
 *  title: string,
 *  image: File,
 *  body: string,
 *  published: boolean
 * }} data
 * @returns {Promise<number>} the id of the created article.
 * @throws {{ status: number, error: Error }} status code and error.
 */
async function createNewsArticle({ authorName, title, image, body, published }) {
  const fileUrl = await uploadFile({ file: image });
  const payload = JSON.stringify({
    authorName: authorName,
    title: title,
    imageUrl: fileUrl,
    body: body,
    published: published,
  });

  const response = await apiFetch("/api/admin/news", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: payload,
  });

  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to add news article"),
    };
  }

  const article = await response.json();
  return article.id;
}

/**
 * Updates target article. If a file (image) is provided, the image
 * replaces the old one. If null, the image is not updated.
 * @param image {File|null} the replacement image. Null to keep the same image.
 * @param updatedNewsArticle {UpdateNewsArticleRequest} update details.
 * @param articleId {number} the target article.
 * @returns {Promise<void>}
 * @throws {{ status: number, error: Error }} status code and error.
 */
async function updateNewsArticle(image, articleId, updatedNewsArticle) {
  if (image) {
    updatedNewsArticle.imageUrl = await uploadFile({
      file: image,
    });
  }
  const response = await apiFetch(`/api/admin/news/${articleId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(updatedNewsArticle),
  });
  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to update news article"),
    };
  }
}

/**
 * @param idsToDelete {number[]} the ids of the articles to delete.
 * @returns {Promise<void>} the number of deleted articles.
 * @throws {{ status: number, error: Error }} status code and error.
 */
async function deleteNews(idsToDelete) {
  const response = await apiFetch("/api/admin/news", {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(idsToDelete),
  });

  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to delete news"),
    };
  }
}

/**
 * Publish or unpublish specified article.
 * @param articleId {number} the article to delete.
 * @param publish {boolean} publish or unpublish
 * @returns {Promise<void>}
 * @throws {{ status: number, error: Error }} status code and error.
 */
async function toggleNewsPublished(articleId, publish) {
  const endpoint = publish
    ? `/api/admin/news/${articleId}/publish`
    : `/api/admin/news/${articleId}/unpublish`;

  const response = await apiFetch(endpoint, {
    method: "PUT",
  });

  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to toggle news publish"),
    };
  }
}

/** @returns Promise<Customer> */
async function getCustomer(customerId) {
  const response = await apiFetch(`/api/admin/customers/${customerId}`, {
    method: "GET",
  });
  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to get customer"),
    };
  }
  return Customers.localizeDates(await response.json());
}

/** @returns Promise<Customer[]> */
async function getCustomers() {
  const response = await apiFetch("/api/admin/customers", {
    method: "GET",
  });
  if (!response.ok) {
    throw {
      status: response.status,
      error: new Error("Unable to get customers"),
    };
  }
  /**@type Customer[] */
  const customers = await response.json();
  for (const customer of customers) {
    Customers.localizeDates(customer);
  }
  return customers;
}

/**
 * @param {{
 *   filter: CustomerFilter
 *   page: number,
 *   pageSize: number
 * }} params
 * @returns Promise<CustomerSearchResult>
 */
async function searchCustomers({ filter, page, pageSize }) {
  const queryParams = new URLSearchParams();
  if (filter.companyName) queryParams.append("companyName", filter.companyName);
  if (filter.cityName) queryParams.append("cityName", filter.cityName);
  if (filter.actionType) queryParams.append("actionTypes", filter.actionType);
  if (filter.actionStatus) queryParams.append("actionStatuses", filter.actionStatus);
  queryParams.append("page", page.toString());
  queryParams.append("pageSize", pageSize.toString());

  const url = `/api/admin/customers${queryParams.toString() ? `?${queryParams}` : ""}`;
  const response = await apiFetch(url, {
    method: "GET",
  });
  if (!response.ok) {
    throw {
      status: response.status,
      error: new Error("Unable to search customers"),
    };
  }
  /**@type CustomerSearchResult */
  const searchResult = await response.json();
  for (const customer of searchResult.customers) {
    Customers.localizeDates(customer);
  }
  return searchResult;
}

/** @returns Promise<Customer> */
async function createCustomer(customer) {
  const newCustomer = JSON.parse(JSON.stringify(customer));
  Customers.normalizeDates(newCustomer);
  Customers.orderListItemPositions(newCustomer);
  Customers.normalizeOrgNumber(newCustomer);
  const response = await apiFetch("/api/admin/customers", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(newCustomer),
  });
  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to create customer"),
    };
  }
  return Customers.localizeDates(await response.json());
}

/** @returns Promise<Customer[]> */
async function updateCustomer(customer) {
  const updatedCustomer = JSON.parse(JSON.stringify(customer));
  Customers.normalizeDates(updatedCustomer);
  Customers.orderListItemPositions(updatedCustomer);
  Customers.normalizeOrgNumber(updatedCustomer);
  const response = await apiFetch(`/api/admin/customers/${customer.id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(updatedCustomer),
  });
  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to update customer"),
    };
  }
  return response.json();
}

async function deleteCustomer(customerId) {
  const response = await apiFetch(`/api/admin/customers/${customerId}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to delete customer"),
    };
  }
}

async function sellProposalsOfCustomer(customerId) {
  const response = await apiFetch(`/api/admin/customers/${customerId}/sell-proposals`, {
    method: "GET",
  });
  if (!response.ok) {
    throw {
      status: response.status,
      error: Error(`Unable to get sell proposals of customer with id ${customerId}`),
    };
  }
  return response.json();
}

/**
 * @param {string} orgNum - The organization number of the company.
 * @returns {Promise<[CompanyDetails|null, {status: number, error: string}|null]>} A promise that resolves to a tuple where the first element is the company details object or null, and the second element is an error object with status and error message or null.
 */
async function importCompanyDetails(orgNum) {
  const response = await apiFetch(`/api/admin/companies/${orgNum}`, {
    method: "GET",
  });
  if (!response.ok) {
    try {
      const json = await response.json();
      const error = json.detail ? json.detail : "-";
      return [
        null,
        {
          status: response.status,
          error: `Bolagsverket svarade med detta fel: ${error}`,
        },
      ];
    } catch (_e) {
      // JSON parsing failed, continue to generic error below
    }
    return [
      null,
      {
        status: response.status,
        error: `Unable to import company details with organization number: ${orgNum}`,
      },
    ];
  }
  return [await response.json(), null];
}

async function getSiteContent(locale) {
  const params = locale ? `?locale=${encodeURIComponent(locale)}` : "";
  const response = await apiFetch(`/api/site-content${params}`);
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to fetch site content") };
  }
  return response.json();
}

async function getAllSiteContentAdmin() {
  const response = await apiFetch("/api/admin/site-content", {});
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to fetch site content") };
  }
  return response.json();
}

async function upsertSiteContent(key, contentType, value, locale) {
  const response = await apiFetch(`/api/admin/site-content/${encodeURIComponent(key)}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ contentType, value, locale: locale || null }),
  });
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to update site content") };
  }
  return response.json();
}

async function updateSiteContentColor(key, color) {
  const response = await apiFetch(`/api/admin/site-content/${encodeURIComponent(key)}/color`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ color: color || null }),
  });
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to update site content color") };
  }
}

async function deleteSiteContent(key, locale) {
  const localeParam = locale ? `?locale=${encodeURIComponent(locale)}` : "";
  const response = await apiFetch(
    `/api/admin/site-content/${encodeURIComponent(key)}${localeParam}`,
    {
      method: "DELETE",
    }
  );
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to delete site content") };
  }
}

async function getEnabledLanguages() {
  const response = await apiFetch("/api/site-content/languages");
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to fetch languages") };
  }
  return response.json();
}

async function getAllLanguagesAdmin() {
  const response = await apiFetch("/api/admin/languages", {});
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to fetch languages") };
  }
  return response.json();
}

async function upsertLanguage(code, label, enabled) {
  const response = await apiFetch(`/api/admin/languages/${encodeURIComponent(code)}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ label, enabled }),
  });
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to upsert language") };
  }
  return response.json();
}

async function translateText(text, sourceLang, targetLang) {
  const response = await apiFetch("/api/admin/translate", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ text, sourceLang, targetLang }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw { status: response.status, error: new Error(err.error || "Translation failed") };
  }
  return response.json();
}

async function getMachineTranslationCoverage() {
  const response = await apiFetch("/api/admin/machine-translations/coverage", {});
  if (!response.ok) {
    throw { status: response.status, error: Error("Failed to fetch coverage") };
  }
  return response.json();
}

async function getMachineTranslations(machineId) {
  const response = await apiFetch(`/api/admin/machines/${machineId}/translations`, {});
  if (!response.ok) {
    throw { status: response.status, error: Error("Failed to fetch translations") };
  }
  return response.json();
}

async function upsertMachineTranslation(machineId, locale, data) {
  const response = await apiFetch(
    `/api/admin/machines/${machineId}/translations/${encodeURIComponent(locale)}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }
  );
  if (!response.ok) {
    throw { status: response.status, error: Error("Failed to save translation") };
  }
  return response;
}

async function getNewsTranslations(newsId) {
  const response = await apiFetch(`/api/admin/news/${newsId}/translations`, {});
  if (!response.ok) {
    throw { status: response.status, error: Error("Failed to fetch news translations") };
  }
  return response.json();
}

async function upsertNewsTranslation(newsId, locale, data) {
  const response = await apiFetch(
    `/api/admin/news/${newsId}/translations/${encodeURIComponent(locale)}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }
  );
  if (!response.ok) {
    throw { status: response.status, error: Error("Failed to save news translation") };
  }
  return response;
}

async function getJobs(locale = "sv") {
  const response = await apiFetch(`/api/jobs?language=${encodeURIComponent(locale)}`);
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to get jobs") };
  }
  return response.json();
}

async function getJob(slug, locale = "sv") {
  const response = await apiFetch(
    `/api/jobs/${encodeURIComponent(slug)}?language=${encodeURIComponent(locale)}`
  );
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to get job") };
  }
  return response.json();
}

async function getAdminJobs() {
  const response = await apiFetch("/api/admin/jobs", {});
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to get admin jobs") };
  }
  return response.json();
}

async function createJob(data) {
  const response = await apiFetch("/api/admin/jobs", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to create job") };
  }
  return response.json();
}

async function updateJob(id, data) {
  const response = await apiFetch(`/api/admin/jobs/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to update job") };
  }
  return response.json();
}

async function deleteJob(id) {
  const response = await apiFetch(`/api/admin/jobs/${id}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to delete job") };
  }
}

async function getJobTranslations(id) {
  const response = await apiFetch(`/api/admin/jobs/${id}/translations`, {});
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to get job translations") };
  }
  return response.json();
}

async function upsertJobTranslation(id, locale, data) {
  const response = await apiFetch(
    `/api/admin/jobs/${id}/translations/${encodeURIComponent(locale)}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }
  );
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to save job translation") };
  }
  return response;
}

async function reorderJobs(items) {
  const response = await apiFetch("/api/admin/jobs/reorder", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items }),
  });
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to reorder jobs") };
  }
}

async function getTeamMembers(locale = "sv") {
  const response = await apiFetch(`/api/team?language=${encodeURIComponent(locale)}`);
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to get team members") };
  }
  return response.json();
}

async function getAdminTeamMembers() {
  const response = await apiFetch("/api/admin/team", {});
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to get admin team members") };
  }
  return response.json();
}

async function createTeamMember(data) {
  const response = await apiFetch("/api/admin/team", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to create team member") };
  }
  return response.json();
}

async function updateTeamMember(id, data) {
  const response = await apiFetch(`/api/admin/team/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to update team member") };
  }
  return response.json();
}

async function deleteTeamMember(id) {
  const response = await apiFetch(`/api/admin/team/${id}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to delete team member") };
  }
}

async function getTeamMemberTranslations(id) {
  const response = await apiFetch(`/api/admin/team/${id}/translations`, {});
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to get team member translations") };
  }
  return response.json();
}

async function upsertTeamMemberTranslation(id, locale, data) {
  const response = await apiFetch(
    `/api/admin/team/${id}/translations/${encodeURIComponent(locale)}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }
  );
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to save team member translation") };
  }
  return response;
}

async function reorderTeamMembers(items) {
  const response = await apiFetch("/api/admin/team/reorder", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items }),
  });
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to reorder team members") };
  }
}

async function getFaq(locale = "sv") {
  const response = await apiFetch(`/api/faq?language=${encodeURIComponent(locale)}`);
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to get FAQ") };
  }
  return await response.json();
}

async function getAdminFaq() {
  const response = await apiFetch("/api/admin/faq", {});
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to get admin FAQ") };
  }
  return await response.json();
}

async function createFaqItem(data) {
  const response = await apiFetch("/api/admin/faq", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to create FAQ item") };
  }
  return await response.json();
}

async function updateFaqItem(id, data) {
  const response = await apiFetch(`/api/admin/faq/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to update FAQ item") };
  }
  return await response.json();
}

async function deleteFaqItem(id) {
  const response = await apiFetch(`/api/admin/faq/${id}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to delete FAQ item") };
  }
}

async function reorderFaqItems(items) {
  const response = await apiFetch("/api/admin/faq/reorder", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items }),
  });
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to reorder FAQ items") };
  }
}

async function getFaqTranslations(id) {
  const response = await apiFetch(`/api/admin/faq/${id}/translations`, {});
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to get FAQ translations") };
  }
  return await response.json();
}

async function upsertFaqTranslation(id, locale, data) {
  const response = await apiFetch(
    `/api/admin/faq/${id}/translations/${encodeURIComponent(locale)}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }
  );
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to upsert FAQ translation") };
  }
}

async function getCategories(locale) {
  const params = locale ? `?language=${encodeURIComponent(locale)}` : "";
  const response = await apiFetch(`/api/categories${params}`);
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to get categories") };
  }
  return await response.json();
}

async function getAdminCategories() {
  const response = await apiFetch("/api/admin/categories", {});
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to get admin categories") };
  }
  return await response.json();
}

async function createCategory(slug, name) {
  const response = await apiFetch("/api/admin/categories", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ slug, name }),
  });
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to create category") };
  }
  return await response.json();
}

async function updateCategory(slug, data) {
  const response = await apiFetch(`/api/admin/categories/${encodeURIComponent(slug)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to update category") };
  }
  return await response.json();
}

async function reorderCategories(items) {
  const response = await apiFetch("/api/admin/categories/reorder", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items: items.map((i) => ({ slug: i.id, sortOrder: i.sortOrder })) }),
  });
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to reorder categories") };
  }
}

async function getCategoryTranslations(slug) {
  const response = await apiFetch(
    `/api/admin/categories/${encodeURIComponent(slug)}/translations`,
    {}
  );
  if (!response.ok)
    throw { status: response.status, error: Error("Unable to get category translations") };
  return response.json();
}

async function upsertCategoryTranslation(slug, locale, data) {
  const response = await apiFetch(
    `/api/admin/categories/${encodeURIComponent(slug)}/translations/${encodeURIComponent(locale)}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }
  );
  if (!response.ok)
    throw { status: response.status, error: Error("Unable to upsert category translation") };
}

async function getTerms(locale = "sv") {
  const response = await apiFetch(`/api/terms?language=${encodeURIComponent(locale)}`);
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to get terms") };
  }
  return await response.json();
}

async function getAdminTerms() {
  const response = await apiFetch("/api/admin/terms", {});
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to get admin terms") };
  }
  return await response.json();
}

async function createTermsSection(data) {
  const response = await apiFetch("/api/admin/terms", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to create terms section") };
  }
  return await response.json();
}

async function updateTermsSection(id, data) {
  const response = await apiFetch(`/api/admin/terms/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to update terms section") };
  }
  return await response.json();
}

async function deleteTermsSection(id) {
  const response = await apiFetch(`/api/admin/terms/${id}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to delete terms section") };
  }
}

async function reorderTermsSections(items) {
  const response = await apiFetch("/api/admin/terms/reorder", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items }),
  });
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to reorder terms sections") };
  }
}

async function getTermsTranslations(id) {
  const response = await apiFetch(`/api/admin/terms/${id}/translations`, {});
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to get terms translations") };
  }
  return await response.json();
}

async function upsertTermsTranslation(id, locale, data) {
  const response = await apiFetch(
    `/api/admin/terms/${id}/translations/${encodeURIComponent(locale)}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }
  );
  if (!response.ok) {
    throw { status: response.status, error: Error("Unable to upsert terms translation") };
  }
}

async function deleteCategory(slug) {
  const response = await apiFetch(`/api/admin/categories/${encodeURIComponent(slug)}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    const text = await response.text();
    const message = text || "Unable to delete category";
    throw { status: response.status, error: Error(message) };
  }
}

/**
 * Users for the user admin page. Needs the user_admin role.
 * @param {{ query?: string, page?: number, size?: number }} [params] page is 0-based.
 * @returns {Promise<{ items: { id: string, email: string, verified: boolean, roles: string[] }[], total: number }>}
 * @throws {{ status: number, error: Error }}
 */
async function getUsers({ query = "", page = 0, size = 50 } = {}) {
  const params = new URLSearchParams({ query, page: String(page), size: String(size) });
  const response = await apiFetch(`/api/admin/users?${params}`, {
    method: "GET",
  });

  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to get users"),
    };
  }

  return await response.json();
}

/**
 * Every role a user can be given, with the name to show for it, in the order the API lists them.
 * Needs the user_admin role.
 * @returns {Promise<{ role: string, label: string }[]>}
 * @throws {{ status: number, error: Error }}
 */
async function getRoles() {
  const response = await apiFetch("/api/admin/users/roles", {
    method: "GET",
  });

  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to get roles"),
    };
  }

  return await response.json();
}

/**
 * Replaces all roles of a user. Status 400 for an unknown role, 404 for an unknown user and 409
 * when it would leave nobody with the user_admin role.
 * @param {string} id
 * @param {string[]} roles
 * @returns {Promise<{ id: string, email: string, verified: boolean, roles: string[] }>}
 * @throws {{ status: number, error: Error }}
 */
async function setUserRoles(id, roles) {
  const response = await apiFetch(`/api/admin/users/${encodeURIComponent(id)}/roles`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ roles }),
  });

  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to update roles"),
    };
  }

  return await response.json();
}

/**
 * Revokes every refresh token of a user. Their access token keeps working until it expires,
 * at most 15 minutes.
 * @param {string} id
 * @throws {{ status: number, error: Error }}
 */
async function logoutUserEverywhere(id) {
  const response = await apiFetch(`/api/admin/users/${encodeURIComponent(id)}/logout`, {
    method: "POST",
  });

  if (!response.ok) {
    throw {
      status: response.status,
      error: Error("Unable to log the user out"),
    };
  }
}

export {
  getMachines,
  createSellProposal,
  contact,
  login,
  register,
  refresh,
  logout,
  createMachine,
  deleteMachine,
  getSellProposals,
  addSellProposalEvent,
  createMachineSale,
  deleteMachineSale,
  getMachineSales,
  getRecentSales,
  verifyOtp,
  resendOtp,
  sendForgotPasswordOtp,
  verifyForgotPasswordOtp,
  updateMachine,
  publishMachine,
  unpublishMachine,
  uploadFile,
  createNewsArticle,
  deleteNews,
  updateNewsArticle,
  getNews,
  toggleNewsPublished,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  getCustomer,
  getCustomers,
  searchCustomers,
  sellProposalsOfCustomer,
  importCompanyDetails,
  getSiteContent,
  getAllSiteContentAdmin,
  upsertSiteContent,
  updateSiteContentColor,
  deleteSiteContent,
  getEnabledLanguages,
  getAllLanguagesAdmin,
  upsertLanguage,
  getMachineTranslationCoverage,
  getMachineTranslations,
  upsertMachineTranslation,
  getNewsTranslations,
  upsertNewsTranslation,
  translateText,
  getJobs,
  getJob,
  getAdminJobs,
  createJob,
  updateJob,
  deleteJob,
  getJobTranslations,
  upsertJobTranslation,
  reorderJobs,
  getTeamMembers,
  getAdminTeamMembers,
  createTeamMember,
  updateTeamMember,
  deleteTeamMember,
  getTeamMemberTranslations,
  upsertTeamMemberTranslation,
  reorderTeamMembers,
  getFaq,
  getAdminFaq,
  createFaqItem,
  updateFaqItem,
  deleteFaqItem,
  getFaqTranslations,
  upsertFaqTranslation,
  reorderFaqItems,
  getCategories,
  getAdminCategories,
  createCategory,
  updateCategory,
  reorderCategories,
  deleteCategory,
  getCategoryTranslations,
  upsertCategoryTranslation,
  getTerms,
  getAdminTerms,
  createTermsSection,
  updateTermsSection,
  deleteTermsSection,
  reorderTermsSections,
  getTermsTranslations,
  upsertTermsTranslation,
  getUsers,
  getRoles,
  setUserRoles,
  logoutUserEverywhere,
};
